import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'
import React, { useContext } from 'react'
import omitBy from 'lodash/omitBy'
import isEmpty from 'lodash/isEmpty'
import isUndefined from 'lodash/isUndefined'

import SignupFormQuestionsHandler from 'components/ParticipantProfile/SignupFormQuestionsHandler'
import {
  checkObjectProperty,
  extractSignupForm,
  segregateQuestions,
} from 'lib/Helper'
import { ParticipantProfileContext } from 'containers/ParticipantProfile'
import signupFormResponseAPI from 'api/ApiActions/SignupFormResponse/Action'
import { useAlert } from 'components/Shared/Alert'
import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { validateQuestion } from 'components/ParticipantProfile/QuestionHelper'
import {
  resetErrors,
  setAllAnswers,
  setError,
} from 'redux/slices/SignupFormAnswers'
import { yupConfig } from 'lib/Yup'
import { getTriggerQuestions } from 'lib/participantProfile/Helper'
import { UNPROCESSABLE_ENTITY } from 'lib/participants/Constants'

const SignupFormWrapper = () => {
  yupConfig()
  const { t } = useTranslation()
  const { search } = useLocation()
  const readOnlyMode = new URLSearchParams(search).get('edit') === null
  const [searchParams, setSearchParams] = useSearchParams()
  const { showAlert } = useAlert()
  const answerPayload: IndexType = {}
  const { getParticipant } = React.useContext(
    ParticipantProfileContext,
  ).functions
  const signupFormAnswers = useAppSelector(
    (state) => state.signupAnswers.answers,
  )

  const signupFormQuestions = useAppSelector(
    (state) => state.participantProfileSignupFormQuestions.data,
  )
  const { participantSummary } = useContext(ParticipantProfileContext).state
  const { questions, options } = extractSignupForm(signupFormQuestions)

  const { parentQuestions, conditionalQuestions } = segregateQuestions(
    questions,
    options,
  )

  const answers = signupFormAnswers
  const dispatch = useAppDispatch()
  const questionsInPage: IndexType[] = []

  const switchToReadOnlyMode = () => {
    if (searchParams.has('edit')) {
      searchParams.delete('edit')
      setSearchParams(searchParams)
    }
  }

  const getQuestionsInPage = () => {
    for (let i = 0; i < parentQuestions.length; i++) {
      const question = parentQuestions[i]

      const isTriggerQuestion = checkObjectProperty(
        conditionalQuestions,
        question.id,
      )

      if (isTriggerQuestion && question.id in answers) {
        getTriggerQuestions(
          questionsInPage,
          question,
          conditionalQuestions,
          answers,
        )
      }

      questionsInPage.push(question)
    }
  }

  const handleTaken = (fieldType: string) => {
    const questionId = signupFormQuestions.find(
      (question: { type: string }) => question.type === fieldType,
    ).id
    dispatch(setError({ questionId, error: 'taken' }))
  }

  const handleNoEmail = (fieldType: string) => {
    const questionId = signupFormQuestions.find(
      (question: { type: string }) => question.type === fieldType,
    ).id
    dispatch(setError({ questionId, error: 'notAnEmail' }))
  }

  const prepAnswerPayload = () => {
    for (let i = 0; i < questionsInPage.length; i++) {
      const question = questionsInPage[i]
      answerPayload[question.id] = signupFormAnswers[question.id]
    }
  }

  const handleUpdateSignupForm = async () => {
    let isError = false

    // create a list of questions which are in page

    getQuestionsInPage()

    for (let i = 0; i < questionsInPage.length; i++) {
      const question = questionsInPage[i]

      let error = []

      error = await validateQuestion(answers[question.id], question)
      if (error?.length) {
        isError = true
        // show the error alert
        showAlert({
          title: t('errors.mandatory'),
          type: 'error',
        })
        // dispatch the error message
        dispatch(setError({ questionId: question.id, error }))
      }
    }

    if (!isError) {
      prepAnswerPayload()

      const params = {
        data: {
          attributes: {
            // remove undefined values before sending it to server
            signup_form_response: omitBy(
              answerPayload,
              (v) => isUndefined(v) || isEmpty(v),
            ),
          },
        },
      }
      const userId = participantSummary.id

      const data = {
        params,
        userId,
      }

      const response = await signupFormResponseAPI(data)

      if (response.status === 200) {
        switchToReadOnlyMode()

        showAlert({
          title: t('participant.profile.signup.success'),
          type: 'success',
        })
        getParticipant()
      } else {
        const { response: res } = response

        if (res?.status === UNPROCESSABLE_ENTITY) {
          const serverErrors = JSON.parse(res?.request?.response).errors

          /**
           * There are only 3 use case scenarios
           *
           * 1. email is taken
           * 2. login is taken
           * 3. email and login both are taken ( array will contain only 2 elements hence no loop
           * and the sequence will be login followed by email )
           */

          if (serverErrors.length > 1) {
            // if both login and email are used
            const loginError = serverErrors[0].source.pointer
              .split('/')
              .indexOf('login')
            const emailError = serverErrors[1].source.pointer
              .split('/')
              .indexOf('email')

            if (loginError && emailError) {
              handleTaken('SignupForm::LoginQuestion')
              handleTaken('SignupForm::AccountEmailQuestion')
            }

            return
          }

          const error = serverErrors[0]
          const pointerValue = error.source.pointer.split('/')
          const isLogin = pointerValue.indexOf('login') !== -1
          const isEmail = pointerValue.indexOf('email') !== -1

          if (isLogin) {
            // find the login question id
            handleTaken('SignupForm::LoginQuestion')
          }

          if (isLogin && error.code === 'cannot_be_email_id') {
            handleNoEmail('SignupForm::LoginQuestion')
          }

          if (isEmail) {
            // find the login question id
            handleTaken('SignupForm::AccountEmailQuestion')
          }
        }
      }
    }
  }

  const initialSignupFormAnswers = useAppSelector(
    (state) => state.signupAnswers.initialAnswers,
  )

  const handleCancelButton = () => {
    switchToReadOnlyMode()
    dispatch(resetErrors())
    dispatch(setAllAnswers(initialSignupFormAnswers))
  }

  const renderUpdateButtons = () => {
    if (!readOnlyMode) {
      return (
        <Stack direction="row" spacing={4} justifyContent={'space-between'}>
          <Button variant="outlined" onClick={handleCancelButton}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleUpdateSignupForm}>
            {t('common.save')}
          </Button>
        </Stack>
      )
    }
  }

  return (
    <Box>
      <Stack>
        {renderUpdateButtons()}
        <SignupFormQuestionsHandler
          questions={parentQuestions}
          conditionalQuestions={conditionalQuestions}
          options={options}
        />
      </Stack>
    </Box>
  )
}

export default SignupFormWrapper
